import React, { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { BEFORE_AFTER } from "../../data";

const Slider = ({ item, testId }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onDown = (e) => { dragging.current = true; setFromClientX(e.clientX ?? e.touches?.[0]?.clientX); };
  const onMove = (e) => { if (dragging.current) setFromClientX(e.clientX ?? e.touches?.[0]?.clientX); };
  const onUp = () => { dragging.current = false; };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
      className="rounded-[1.5rem] overflow-hidden border border-[rgba(19,57,85,0.08)] shadow-[0_8px_32px_rgba(19,57,85,0.08)] bg-white"
    >
      <div
        ref={ref}
        className="relative aspect-[3/2] w-full select-none cursor-ew-resize touch-none"
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={onUp}
        role="slider"
        aria-label={`${item.title} before and after comparison`}
        aria-valuenow={Math.round(pos)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
          if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
        }}
      >
        {/* After (base) */}
        <img src={item.after} alt={`${item.title} after`} loading="lazy" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable="false" />
        <span className="absolute top-3 right-3 z-10 rounded-full bg-[#54BCE6] text-[#133955] text-xs font-bold px-3 py-1">AFTER</span>

        {/* Before (clipped overlay via clip-path so both images stay aligned) */}
        <div className="absolute inset-0 pointer-events-none" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img src={item.before} alt={`${item.title} before`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
          <span className="absolute top-3 left-3 rounded-full bg-[#133955] text-white text-xs font-bold px-3 py-1">BEFORE</span>
        </div>

        {/* Handle */}
        <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
          <div className="w-0.5 h-full bg-white shadow-[0_0_0_1px_rgba(19,57,85,0.2)]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 rounded-full bg-white shadow-[0_4px_16px_rgba(19,57,85,0.3)] text-[#133955]">
            <MoveHorizontal size={18} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between px-5 py-4">
        <p className="font-display text-lg font-bold text-[#133955]">{item.title}</p>
        <span className="text-xs font-semibold uppercase tracking-wide text-[#54BCE6]">{item.tag}</span>
      </div>
    </motion.div>
  );
};

export const BeforeAfter = () => {
  return (
    <section id="gallery" data-testid="gallery-section" className="bg-white py-16 md:py-24 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-10 md:mb-14">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.24em] font-bold text-[#54BCE6] mb-4"
          >
            Before &amp; after
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            className="font-display text-[#133955] font-extrabold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
          >
            See the Kind of Transformations Possible.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-5 text-base md:text-lg text-[#173042]/75 max-w-xl"
          >
            Drag each slider to reveal the possibilities. These are design examples, not
            completed Home Pro Connect customer projects.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {BEFORE_AFTER.map((item, i) => (
            <Slider key={item.title} item={item} testId={`before-after-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
};
